import { useState } from 'react';
import type { DashboardSummary } from '../types/dashboard';

interface ConfirmationDeleteModalProps {
  dashboard: DashboardSummary;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

function isBundleLevel(sourceKey: string): boolean {
  const parts = sourceKey.split(':');
  // standalone:dashboard:uuid has 3 parts but starts with "standalone"
  // entity-specific: node:process:uuid has 3 parts
  // bundle-level: node:process has 2 parts
  if (parts.length === 2) return true;
  if (sourceKey.startsWith('standalone:')) return false;
  return false;
}

function getBundleName(sourceKey: string): string {
  const parts = sourceKey.split(':');
  return parts[1] || sourceKey;
}

export function ConfirmationDeleteModal({ dashboard, onConfirm, onCancel, loading }: ConfirmationDeleteModalProps) {
  const [deleteInput, setDeleteInput] = useState('');
  const bundleLevel = isBundleLevel(dashboard.sourceKey);
  const isStandalone = dashboard.type === 'standalone';

  const canConfirm = bundleLevel ? deleteInput === 'DELETE' : true;

  const title = bundleLevel
    ? `Удалить общий дашборд для всех «${getBundleName(dashboard.sourceKey)}»?`
    : isStandalone
      ? `Удалить дашборд «${dashboard.title || dashboard.sourceKey}»?`
      : 'Удалить дашборд процесса?';

  const description = bundleLevel
    ? 'Этот дашборд используется для ВСЕХ процессов данного типа. Удаление затронет все процессы. Действие нельзя отменить.'
    : isStandalone
      ? 'Дашборд будет удалён без возможности восстановления.'
      : 'Дашборд будет удалён, данные процесса не пострадают. При необходимости можно создать новый дашборд для этого процесса.';

  return (
    <div className="modal show d-block" tabIndex={-1} role="dialog" aria-label="Удалить дашборд" aria-modal="true">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-danger">
          <div className="modal-header text-bg-danger">
            <h5 className="modal-title">
              <i className="las la-trash-alt me-2" />
              Удалить дашборд
            </h5>
            <button type="button" className="btn-close btn-close-white" aria-label="Закрыть" onClick={onCancel} disabled={loading} />
          </div>
          <div className="modal-body">
            <p className="fw-semibold">{title}</p>
            <p className="text-muted mb-0">{description}</p>
            {bundleLevel && (
              <div className="mt-3">
                <label htmlFor="delete-confirm-input" className="form-label text-danger fw-semibold">
                  Введите <code>DELETE</code> для подтверждения:
                </label>
                <input
                  type="text"
                  id="delete-confirm-input"
                  className="form-control"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder="DELETE"
                  disabled={loading}
                  autoComplete="off"
                  data-testid="delete-confirm-input"
                />
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
              Отмена
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={onConfirm}
              disabled={!canConfirm || loading}
              data-testid="delete-confirm-btn"
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  Удаление...
                </>
              ) : (
                <>
                  <i className="las la-trash-alt me-1" />
                  Удалить
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop show"
        onClick={onCancel}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1 }}
      />
    </div>
  );
}
